import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { toast } from "../utils/toast";
import {
  addCartItem,
  fetchCart,
  removeCartItem,
  updateCartItem,
} from "../services/cartService";
import { CartApiItem, CartItem, Product } from "../types/store";
import { useAuth } from "./AuthContext";
import { resolveProductImageUrl } from "../utils/productImageUrl";

interface CartContextValue {
  items: CartItem[];
  addToCart: (product: Product, quantity?: number) => Promise<boolean>;
  removeFromCart: (productId: number) => Promise<void>;
  updateQuantity: (productId: number, quantity: number) => Promise<void>;
  clearCart: () => void;
  itemCount: number;
  subtotal: number;
  loading: boolean;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);
const CART_STORAGE_KEY = "eldoo-cart-local";
const LOGIN_PATH = "/login";

const redirectToLogin = () => {
  const redirect = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`${LOGIN_PATH}?redirect=${encodeURIComponent(redirect)}`);
};

const getCartErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    const responseMessage = error.response?.data?.message;
    const isInternalMessage =
      typeof responseMessage === "string" &&
      /(transaction|unsaved-value|entity\.|hibernate|sql|database|optimistic locking)/i.test(
        responseMessage,
      );
    if (typeof responseMessage === "string" && responseMessage.trim() && !isInternalMessage) {
      return responseMessage;
    }
  }

  return fallback;
};

const isAuthorizationError = (error: unknown) =>
  error instanceof AxiosError &&
  (error.response?.status === 401 || error.response?.status === 403);

const clampQuantityToStock = (requestedQuantity: number, stockQuantity: number) => {
  const availableStock = Math.max(0, Math.floor(stockQuantity));
  if (availableStock <= 0) {
    return 0;
  }

  return Math.max(1, Math.min(Math.floor(requestedQuantity), availableStock));
};

const getStockLimitMessage = (stockQuantity: number) =>
  stockQuantity > 0
    ? `Stock not available. Only ${stockQuantity} item${stockQuantity === 1 ? "" : "s"} are in stock.`
    : "This item is out of stock.";

const toCartItem = (item: CartApiItem): CartItem => ({
  id: item.id,
  quantity: item.quantity,
  product: {
    id: item.productId,
    slug: item.productSlug,
    name: item.productName,
    brand: item.brand,
    category: "",
    categorySlug: "",
    subcategory: "",
    subcategorySlug: "",
    subcategoryId: 0,
    price: item.unitPrice,
    originalPrice: item.originalPrice,
    discountPercentage: item.discountPercentage,
    stockQuantity: item.stockQuantity,
    lowStock: false,
    availability:
      item.stockQuantity <= 0
        ? "out-of-stock"
        : item.stockQuantity <= 5
        ? "low-stock"
        : "in-stock",
    badge: "",
    shortDescription: "",
    description: "",
    heroTag: "",
    images: item.image ? [resolveProductImageUrl(item.image)] : [],
    tags: [],
    featured: false,
    bestSeller: false,
    bulkEligible: false,
    newArrival: false,
    warrantyAvailable: false,
    replacementAvailable: item.stockQuantity > 0,
  },
});

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const useRemoteCart = isAuthenticated && user?.role === "ROLE_CUSTOMER";

  const loadRemoteCart = useCallback(async () => {
    if (!useRemoteCart) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const cart = await fetchCart();
      setItems(cart.items.map(toCartItem));
    } catch (error) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [useRemoteCart]);

  useEffect(() => {
    if (useRemoteCart) {
      void loadRemoteCart();
      return;
    }

    const storedItems = window.localStorage.getItem(CART_STORAGE_KEY);
    if (storedItems) {
      setItems(JSON.parse(storedItems));
    } else {
      setItems([]);
    }
  }, [loadRemoteCart, useRemoteCart]);

  useEffect(() => {
    if (!useRemoteCart) {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    }
  }, [items, useRemoteCart]);

  const addToCart = async (product: Product, quantity = 1) => {
    if (!useRemoteCart) {
      setItems([]);
      redirectToLogin();
      return false;
    }

    const requestedQuantity = clampQuantityToStock(quantity, product.stockQuantity);
    if (requestedQuantity <= 0) {
      toast.error(getStockLimitMessage(product.stockQuantity));
      return false;
    }

    setItems((currentItems) => {
      const existingItem = currentItems.find((item) => item.product.id === product.id);
      if (existingItem) {
        const nextRequestedQuantity = existingItem.quantity + requestedQuantity;
        if (nextRequestedQuantity > product.stockQuantity) {
          toast.error(getStockLimitMessage(product.stockQuantity));
        }
        const nextQuantity = clampQuantityToStock(nextRequestedQuantity, product.stockQuantity);
        return currentItems.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: nextQuantity }
            : item
        );
      }
      return [...currentItems, { product, quantity: requestedQuantity }];
    });

    try {
      const cart = await addCartItem(product.id, requestedQuantity);
      setItems(cart.items.map(toCartItem));
    } catch (error) {
      if (isAuthorizationError(error)) {
        setItems([]);
        redirectToLogin();
        return false;
      }

      toast.error(getCartErrorMessage(error, "Couldn't add this item to cart right now."));
      return false;
    }

    return true;
  };

  const removeFromCart = async (productId: number) => {
    if (useRemoteCart) {
      const item = items.find((cartItem) => cartItem.product.id === productId);
      if (item?.id) {
        const previousItems = items;
        setItems((currentItems) =>
          currentItems.filter((cartItem) => cartItem.product.id !== productId)
        );
        try {
          const cart = await removeCartItem(item.id);
          setItems(cart.items.map(toCartItem));
        } catch (error) {
          setItems(previousItems);
          if (isAuthorizationError(error)) {
            setItems([]);
            return;
          }

          toast.error(getCartErrorMessage(error, "Couldn't remove this item from cart right now."));
          return;
        }

        return;
      }
      return;
    }

    setItems((currentItems) =>
      currentItems.filter((item) => item.product.id !== productId)
    );
  };

  const updateQuantity = async (productId: number, quantity: number) => {
    const existingItem = items.find((cartItem) => cartItem.product.id === productId);
    const stockQuantity = existingItem?.product.stockQuantity ?? 0;
    if (quantity > stockQuantity) {
      toast.error(getStockLimitMessage(stockQuantity));
    }
    const requestedQuantity = clampQuantityToStock(quantity, stockQuantity);

    if (requestedQuantity <= 0) {
      await removeFromCart(productId);
      return;
    }
    if (useRemoteCart) {
      const item = existingItem;
      if (item?.id) {
        const previousItems = items;
        setItems((currentItems) =>
          currentItems.map((cartItem) =>
            cartItem.product.id === productId ? { ...cartItem, quantity: requestedQuantity } : cartItem,
          ),
        );
        try {
          const cart = await updateCartItem(item.id, productId, requestedQuantity);
          setItems(cart.items.map(toCartItem));
        } catch (error) {
          setItems(previousItems);
          if (isAuthorizationError(error)) {
            setItems([]);
            return;
          }

          toast.error(getCartErrorMessage(error, "Couldn't update cart quantity right now."));
        }
      }
      return;
    }

    setItems((currentItems) =>
      currentItems.map((item) =>
        item.product.id === productId ? { ...item, quantity: requestedQuantity } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
    window.localStorage.removeItem(CART_STORAGE_KEY);
  };

  const itemCount = useMemo(
    () => items.reduce((total, item) => total + item.quantity, 0),
    [items]
  );

  const subtotal = useMemo(
    () => items.reduce((total, item) => total + item.product.price * item.quantity, 0),
    [items]
  );

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        itemCount,
        subtotal,
        loading,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};
